/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Deployments API
 * 
 * This module provides API functions for managing product deployments.
 * Deployments are hosted environments (dev, test, staging, production, demo)
 * for Velocity products.
 */

import { buildProductApiUrl } from './base';
import type {
  Deployment,
  DeploymentStatus,
  EnvironmentType,
  DeploymentsListResult,
  DeploymentResult,
  DeploymentOperationResult,
  DeploymentEventsResult,
  DeploymentSecretsResult,
  DeploymentMetricsResult,
  ResourceTemplatesResult,
  DeploymentLogsResult,
  CreateDeploymentRequest,
  UpdateDeploymentRequest,
  ListDeploymentsParams,
  DeploymentLogsRequest,
} from '../../../server/types';

// ============================================================================
// List & Get Operations
// ============================================================================

/**
 * List all deployments, optionally filtered
 */
export async function listDeployments(
  params?: ListDeploymentsParams
): Promise<DeploymentsListResult> {
  try {
    const url = buildProductApiUrl('/api/deployments', params as Record<string, string>);
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch deployments',
    };
  }
}

/**
 * Get a single deployment by ID
 */
export async function getDeployment(deploymentId: string): Promise<DeploymentResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}`);
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch deployment',
    };
  }
}

/**
 * Get resource templates (sizing presets)
 */
export async function getResourceTemplates(): Promise<ResourceTemplatesResult> {
  try {
    const url = buildProductApiUrl('/api/deployments/templates');
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch templates',
    };
  }
}

// ============================================================================
// Create & Update Operations
// ============================================================================

/**
 * Create a new deployment
 */
export async function createDeployment(
  request: CreateDeploymentRequest
): Promise<DeploymentResult> {
  try {
    const url = buildProductApiUrl('/api/deployments');
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create deployment',
    };
  }
}

/**
 * Update a deployment
 */
export async function updateDeployment(
  deploymentId: string,
  request: UpdateDeploymentRequest
): Promise<DeploymentResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}`);
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update deployment',
    };
  }
}

/**
 * Delete a deployment
 */
export async function deleteDeployment(
  deploymentId: string
): Promise<DeploymentOperationResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}`);
    const response = await fetch(url, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete deployment',
    };
  }
}

// ============================================================================
// Deployment Operations (Start/Stop/Deploy/etc.)
// ============================================================================

/**
 * Start a stopped deployment
 */
export async function startDeployment(
  deploymentId: string
): Promise<DeploymentOperationResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/start`);
    const response = await fetch(url, { method: 'POST' });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to start deployment',
    };
  }
}

/**
 * Stop a running deployment
 */
export async function stopDeployment(
  deploymentId: string
): Promise<DeploymentOperationResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/stop`);
    const response = await fetch(url, { method: 'POST' });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to stop deployment',
    };
  }
}

/**
 * Restart a deployment
 */
export async function restartDeployment(
  deploymentId: string
): Promise<DeploymentOperationResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/restart`);
    const response = await fetch(url, { method: 'POST' });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to restart deployment',
    };
  }
}

/**
 * Deploy/update deployment from git
 */
export async function deployFromGit(
  deploymentId: string,
  options?: { branch?: string; commit?: string }
): Promise<DeploymentOperationResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/deploy`);
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options || {}),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to deploy',
    };
  }
}

/**
 * Rollback deployment to a previous version
 */
export async function rollbackDeployment(
  deploymentId: string,
  options: { commitSha?: string; tag?: string }
): Promise<DeploymentOperationResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/rollback`);
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to rollback deployment',
    };
  }
}

// ============================================================================
// Monitoring & Logs
// ============================================================================

/**
 * Get deployment events/history
 */
export async function getDeploymentEvents(
  deploymentId: string,
  limit?: number
): Promise<DeploymentEventsResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/events`, { limit });
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch deployment events',
    };
  }
}

/**
 * Get deployment metrics
 */
export async function getDeploymentMetrics(
  deploymentId: string,
  timeRange?: '1h' | '6h' | '24h' | '7d' | '30d'
): Promise<DeploymentMetricsResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/metrics`, { timeRange });
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch deployment metrics',
    };
  }
}

/**
 * Get deployment logs
 */
export async function getDeploymentLogs(
  deploymentId: string,
  options?: DeploymentLogsRequest
): Promise<DeploymentLogsResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/logs`, options as Record<string, string>);
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch deployment logs',
    };
  }
}

// ============================================================================
// Secrets Management
// ============================================================================

/**
 * Get deployment secrets (keys only, not values)
 */
export async function getDeploymentSecrets(
  deploymentId: string
): Promise<DeploymentSecretsResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/secrets`);
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch deployment secrets',
    };
  }
}

/**
 * Set a deployment secret
 */
export async function setDeploymentSecret(
  deploymentId: string,
  key: string,
  value: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/secrets/${key}`);
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to set secret',
    };
  }
}

/**
 * Delete a deployment secret
 */
export async function deleteDeploymentSecret(
  deploymentId: string,
  key: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/secrets/${key}`);
    const response = await fetch(url, { method: 'DELETE' });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete secret',
    };
  }
}

// ============================================================================
// Database Operations
// ============================================================================

/**
 * Run database migrations
 */
export async function runDatabaseMigrations(
  deploymentId: string
): Promise<DeploymentOperationResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/db/migrate`);
    const response = await fetch(url, { method: 'POST' });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to run migrations',
    };
  }
}

/**
 * Create database backup
 */
export async function createDatabaseBackup(
  deploymentId: string
): Promise<DeploymentOperationResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/db/backup`);
    const response = await fetch(url, { method: 'POST' });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create backup',
    };
  }
}

/**
 * Restore database from backup
 */
export async function restoreDatabase(
  deploymentId: string,
  backupId: string
): Promise<DeploymentOperationResult> {
  try {
    const url = buildProductApiUrl(`/api/deployments/${deploymentId}/db/restore`);
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ backupId }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to restore database',
    };
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get status color for deployment status
 */
export function getDeploymentStatusColor(status: DeploymentStatus): 'green' | 'yellow' | 'red' | 'gray' {
  switch (status) {
    case 'running':
      return 'green';
    case 'starting':
    case 'stopping':
    case 'deploying':
    case 'provisioning':
    case 'updating':
    case 'rolling-back':
      return 'yellow';
    case 'stopped':
    case 'pending':
    case 'deleted':
      return 'gray';
    case 'failed':
    case 'unhealthy':
    case 'deleting':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Get human-readable status label
 */
export function getDeploymentStatusLabel(status: DeploymentStatus): string {
  switch (status) {
    case 'pending': return 'Pending';
    case 'provisioning': return 'Provisioning';
    case 'deploying': return 'Deploying';
    case 'starting': return 'Starting';
    case 'running': return 'Running';
    case 'unhealthy': return 'Unhealthy';
    case 'stopping': return 'Stopping';
    case 'stopped': return 'Stopped';
    case 'updating': return 'Updating';
    case 'rolling-back': return 'Rolling Back';
    case 'failed': return 'Failed';
    case 'deleting': return 'Deleting';
    case 'deleted': return 'Deleted';
    default: return status;
  }
}

/**
 * Get environment type label
 */
export function getEnvironmentTypeLabel(type: EnvironmentType): string {
  switch (type) {
    case 'dev': return 'Development';
    case 'test': return 'Test';
    case 'staging': return 'Staging';
    case 'production': return 'Production';
    case 'demo': return 'Demo';
    default: return type;
  }
}

/**
 * Format cost as currency
 */
export function formatCost(cost: number | undefined): string {
  if (cost === undefined || cost === null) return '--';
  return `$${cost.toFixed(2)}`;
}

/**
 * Format uptime duration
 */
export function formatUptime(uptimeSeconds: number | undefined): string {
  if (uptimeSeconds === undefined || uptimeSeconds === null) return '--';
  
  const days = Math.floor(uptimeSeconds / 86400);
  const hours = Math.floor((uptimeSeconds % 86400) / 3600);
  const minutes = Math.floor((uptimeSeconds % 3600) / 60);
  
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

